// Single-line summary above the table when any insurance filter or search
// is active. Mirrors the deliverable ActiveFilterSummary pattern: surfaces
// active signals as readable prose plus a Clear-all affordance so users can
// reset every chip + search in one click. Renders only when there's at
// least one active signal.

import { INSURANCE_CATEGORY_LABELS } from "../../../../types";
import type {
  InsuranceCategory,
  InsuranceStatus,
} from "../../../../types";
import type { InsuranceFilterChipBarFilters } from "./InsuranceFilterChipBar";

export interface InsuranceActiveFilterSummaryProps {
  filters: InsuranceFilterChipBarFilters;
  search: string;
  onClearAll: () => void;
}

const STATUS_LABELS: Record<InsuranceStatus, string> = {
  upcoming: "Future",
  current: "Current",
  expiring: "Expiring soon",
  expired: "Expired",
};

function joinList<T extends string>(
  values: T[],
  labels: Record<T, string>,
): string {
  return values.map((v) => labels[v]).join(", ");
}

function signalCount(
  filters: InsuranceFilterChipBarFilters,
  search: string,
): number {
  let count = 0;
  if (filters.category.length > 0) count += 1;
  if (filters.status.length > 0) count += 1;
  if (filters.provider.length > 0) count += 1;
  if (filters.evidenceMissing) count += 1;
  if (filters.belowMinimum) count += 1;
  if (filters.includeArchived) count += 1;
  if (search.trim().length > 0) count += 1;
  return count;
}

export default function InsuranceActiveFilterSummary({
  filters,
  search,
  onClearAll,
}: InsuranceActiveFilterSummaryProps) {
  const trimmedSearch = search.trim();
  if (signalCount(filters, search) === 0) return null;

  const parts: string[] = [];
  if (filters.category.length > 0) {
    parts.push(
      `Type: ${joinList<InsuranceCategory>(
        filters.category,
        INSURANCE_CATEGORY_LABELS,
      )}`,
    );
  }
  if (filters.status.length > 0) {
    parts.push(`Status: ${joinList(filters.status, STATUS_LABELS)}`);
  }
  if (filters.provider.length > 0) {
    parts.push(`Provider: ${filters.provider.join(", ")}`);
  }
  if (filters.evidenceMissing) parts.push("Evidence missing");
  if (filters.belowMinimum) parts.push("Below minimum");
  if (filters.includeArchived) parts.push("Include archived");
  if (trimmedSearch) parts.push(`Search: '${trimmedSearch}'`);

  return (
    <div
      data-testid="insurance-active-filter-summary"
      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-caption text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-300"
    >
      <span>{parts.join(" · ")}</span>
      <button
        type="button"
        onClick={onClearAll}
        className="text-caption font-medium text-brand-600 hover:underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-brand-400"
      >
        Clear all
      </button>
    </div>
  );
}
